import { useMemo } from "react"

import { PaymentSystemType, Psychologist } from "utils/interfaces"
import { Analytics } from "utils/analytics"
import { isBookingPage } from "app/AppRouter/utils"
import { YooKassaWidget } from "components/PaymentWidget/YooKassa"
import { CloudPaymentsWidget } from "components/PaymentWidget/CloudPayments"
import { useSuccessfulPaymentUrl } from "components/PaymentWidget/useSuccessfulPaymentUrl"
import { getUrlForGazprompay, getUrlForStripe } from "components/PaymentWidget/helper"
import { paymentStateType } from "modules/Booking/PaymentPage/constants"
import { SESSION_TYPE } from "modules/Booking/common/constants"

interface Props {
    payment_system: PaymentSystemType | undefined
    booking_id: string | number
    order_id: string | number
    order_hash: string
    psy: {
        hashId: string
        displayName: string
    }
    booking_hash: string
    deposit_hash?: string
    time_request_hash?: string
    price_usd: string | number
    sessionType?: SESSION_TYPE | undefined
    price?: string | number
    onFail?: (reason: unknown) => void
    onError: (message: string) => void
    onSuccess?: () => void
    changePaymentPageState?: (state: paymentStateType) => void
}

export function PaymentWidget(props: Props) {
    const {
        payment_system,
        booking_id,
        order_hash,
        psy,
        booking_hash,
        deposit_hash,
        time_request_hash,
        price_usd,
        changePaymentPageState,
    } = props

    const initUrl = useMemo(() => {
        if (order_hash) {
            return `/payment/order/${order_hash}/init`
        } else return
    }, [order_hash])

    const returnUrl = useSuccessfulPaymentUrl({
        hash_id: psy.hashId,
        booking_hash,
        deposit_hash,
        time_request_hash,
        order_hash,
    })

    const handleOnSuccess = () => {
        if (isBookingPage()) {
            window.location.href = returnUrl
        } else {
            props.onSuccess && props.onSuccess()
        }
    }

    function onWidgetShown() {
        Analytics.payment_widget_shown({
            booking_id,
            psy,
        })
    }
    switch (payment_system) {
        case PaymentSystemType.CLOUDPAYMENTS:
            changePaymentPageState?.(paymentStateType.tryAgain)
            return (
                <CloudPaymentsWidget
                    {...props}
                    onWidgetShown={() => {
                        changePaymentPageState?.(paymentStateType.wait)
                        onWidgetShown()
                    }}
                    onSuccess={handleOnSuccess}
                    onClose={() => changePaymentPageState?.(paymentStateType.tryAgain)}
                />
            )
        case PaymentSystemType.YOOKASSA_SAFE:
        case PaymentSystemType.YOOKASSA:
        case PaymentSystemType.YOOKASSA_DIRECT:
            return (
                <YooKassaWidget
                    {...props}
                    psyHashId={psy.hashId}
                    initUrl={initUrl}
                    returnUrl={returnUrl}
                    onWidgetShown={onWidgetShown}
                />
            )

        case PaymentSystemType.STRIPE:
            window.location.href = getUrlForStripe(
                returnUrl,
                order_hash,
                payment_system,
                psy,
                price_usd
            )
            changePaymentPageState?.(paymentStateType.tryAgain)
            return null
        case PaymentSystemType.GAZPROMPAY:
            onWidgetShown()
            window.location.href = getUrlForGazprompay(returnUrl, order_hash)
            changePaymentPageState?.(paymentStateType.tryAgain)
            return null

        default:
            return <>Ошибка инициализации страницы оплаты</>
    }
}

function getPsyData(psy?: Psychologist): {
    hashId: string
    displayName: string
} {
    return {
        hashId: psy?.hash_id || "",
        displayName: psy?.display_name || "",
    }
}

PaymentWidget.getPsyData = getPsyData
